import { useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";

import { useGetInvoiceAttachmentBuckets } from "api/queries/invoice/useGetInvoiceAttachmentBuckets";
import { useGetPODAttachmentBuckets } from "api/queries/shipments/useGetPODAttachmentBuckets";
import {
  PodBucketAttachment,
  useGetPodBucketAttachments,
} from "api/queries/shipments/useGetPodBucketAttachments";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ShipmentTypes } from "constants/";
import ProofOfDeliveryAttachmentItem from "./ProofOfDeliveryAttachmentItem";
import { OTHER_BUCKET } from "../pickupDeliveryProofOptions";
import {
  attachmentMatchesBucket,
  isOngoingPodAttachmentReadOnly,
  selectedAttachmentsByBucketId,
} from "../podBucketAttachmentHelpers";

const attachmentBucketSelector = (data: any) => (!data?.results?.length ? OTHER_BUCKET : data.results);

const mapPodBucketAttachmentToFormFile = (item: PodBucketAttachment) => ({
  attachment_id: item.attachment_id,
  file_id: item.file_id,
  fileName: item.filename,
  url: item.url,
  reference: item.reference_number,
  size: item.size,
  bucket_name: item.bucket_name,
});

const ProofOfDeliveryAttachment = function () {
  const { id: shipmentId } = useParams();
  const { control, setValue, getValues } = useFormContext();
  const fileAttachments = useWatch({ control, name: "file_attachments", defaultValue: [] }) ?? [];

  const { data: shipmentData, isLoading: isShipmentLoading }: any = useGetShipmentBasic(shipmentId!, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });

  const isMultistop =
    Boolean(shipmentData?.is_multi_stop_shipment) ||
    shipmentData?.shipment?.shipment_type === ShipmentTypes.MULTISTOP;

  const shouldPersistPodBuckets = Boolean(
    shipmentData?.settings?.pre_delivery_pod_attachments_enabled && shipmentId && !isMultistop,
  );

  const organizationStatus = shipmentData?.organization?.shipment_status;
  const shipmentStatus = shipmentData?.shipment?.shipment_status;
  const isAttachmentReadOnly = isOngoingPodAttachmentReadOnly(organizationStatus, shipmentStatus);

  const { data: podBucketAttachmentsData } = useGetPodBucketAttachments(shipmentId!, {
    enabled: shouldPersistPodBuckets,
  });

  const { data: podAttachmentBuckets = [], isInitialLoading: isPodInitialLoading } = useGetPODAttachmentBuckets({
    filters: { shipment: shipmentId },
    options: {
      enabled: !!shipmentId && shipmentData?.settings?.pod_attachment_buckets,
      select: attachmentBucketSelector,
    },
  });

  const { data: invoiceAttachmentBuckets = [], isInitialLoading: isInvoiceInitialLoading } = useGetInvoiceAttachmentBuckets({
    filters: { shipment: shipmentId },
    options: {
      enabled: !!shipmentId && shipmentData?.settings?.invoice_attachment_buckets,
      select: attachmentBucketSelector,
    },
  });

  const attachmentBuckets = (
    shipmentData?.settings?.pod_attachment_buckets
      ? podAttachmentBuckets
      : shipmentData?.settings?.invoice_attachment_buckets
      ? invoiceAttachmentBuckets
      : OTHER_BUCKET
  ) as any[];

  const isInitialLoading =
    isShipmentLoading || (shipmentData?.settings?.pod_attachment_buckets ? isPodInitialLoading : isInvoiceInitialLoading);

  const filesForBuckets = shouldPersistPodBuckets
    ? (podBucketAttachmentsData?.results ?? []).map(mapPodBucketAttachmentToFormFile)
    : fileAttachments;

  useLayoutEffect(() => {
    if (!shouldPersistPodBuckets) {
      return;
    }

    const results = podBucketAttachmentsData?.results;
    if (results == null) {
      return;
    }

    const nextAttachments = results.map(mapPodBucketAttachmentToFormFile);
    const currentAttachments = getValues("file_attachments") ?? [];
    const isUnchanged =
      currentAttachments.length === nextAttachments.length &&
      currentAttachments.every(
        (item: { file_id?: number; reference?: string }, index: number) =>
          item.file_id === nextAttachments[index].file_id && item.reference === nextAttachments[index].reference,
      );

    const nextSelected = selectedAttachmentsByBucketId(attachmentBuckets, nextAttachments);
    const currentSelected = getValues("selected_attachments") ?? {};
    const selectedUnchanged =
      Object.keys(currentSelected).length === Object.keys(nextSelected).length &&
      Object.keys(nextSelected).every((key) => currentSelected[key] === nextSelected[key]);

    if (isUnchanged && selectedUnchanged) {
      return;
    }

    if (!isUnchanged) {
      setValue("file_attachments", nextAttachments);
    }
    if (!selectedUnchanged) {
      setValue("selected_attachments", nextSelected);
    }
  }, [shouldPersistPodBuckets, podBucketAttachmentsData, attachmentBuckets, setValue, getValues]);

  return (
    <div>
      <div className="text-sm text-secondary-v2 font-medium mb-sm">
        <FormattedMessage
          id="components.molecules.top-nav.attachProofFiles"
          defaultMessage="Attach proof of delivery files"
        />
      </div>
      {!isInitialLoading &&
        !!attachmentBuckets.length &&
        attachmentBuckets.map((attachmentBucket: any) => (
          <ProofOfDeliveryAttachmentItem
            key={attachmentBucket.id}
            bucket={attachmentBucket}
            dataSource={shipmentData?.settings?.pod_attachment_buckets ? "pod" : "invoice"}
            shipmentId={shipmentId!}
            persistToPodBucket={shouldPersistPodBuckets}
            isAttachmentReadOnly={isAttachmentReadOnly}
            bucketFile={filesForBuckets.find((attachment: { attachment_id?: number | null; bucket_name?: string }) =>
              attachmentMatchesBucket(attachment, attachmentBucket),
            )}
          />
        ))}
      {isInitialLoading && <LoadingSkeleton rows={1} />}
    </div>
  );
};

export default ProofOfDeliveryAttachment;
